import { Box, HStack, Spacer, Spinner } from '@chakra-ui/react';
import type React from 'react';
import { HiOutlinePlus } from 'react-icons/hi2';
import FaceliftIconButton from '@/components/Atoms/FaceliftIconButton';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { Tooltip } from '@/components/ui/tooltip';
import usePermission from '@/hooks/usePermission';

const Panel = ({
  title,
  children,
  scoreCard,
  addButton,
}: {
  title: string;
  children: React.ReactNode;
  scoreCard?: React.ReactNode,
  addButton?: {
    title: string,
    permission?: string,
    icon?: React.ReactNode,
    onClick: () => void;
    /** Optional tooltip text; defaults to "Add {title}" */
    tooltip?: string;
  },
}) => {
  const { hasPermission } = usePermission();
  const canShowAdd = !!addButton && hasPermission([addButton?.permission || '']);
  const tooltipText = addButton?.tooltip ?? `Add ${addButton?.title}`;

  return (
    <Box>
      <Box 
        borderWidth="1px"
        borderColor={{
          _light: 'gray.200',
          _dark: "gray.600",
        }}
        borderRadius="2xl"
        boxShadow="sm"
        transition="all 160ms ease"
        my={3}
        _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
      >
        {scoreCard}
      </Box>
      <Box 
        borderWidth="1px"
        borderColor={{
          _light: 'gray.200',
          _dark: "gray.600",
        }}
        borderRadius="lg"
        boxShadow="sm"
        transition="all 160ms ease"
        _hover={{ boxShadow: "md", transform: "translateY(-1px)" }}
      >
      <Box>
        {/* Header: title + optional add action (compact icon, no full-width button) */}
        <HStack px="4" py="3" borderBottomWidth="1px" borderBottomColor={{ _light: "gray.100", _dark: 'gray.600' }}>
          <FaceliftText fontWeight="700" fontSize="md">
            {title}
          </FaceliftText>
          <Spacer />
          <Show when={canShowAdd}>
            <Tooltip
              content={tooltipText}
              positioning={{ placement: 'bottom' }}
              openDelay={0}
              closeDelay={0}
            >
              <FaceliftIconButton
                aria-label={tooltipText}
                borderRadius="full"
                w="8"
                h="8"
                minW="8"
                minH="8"
                p="0"
                flexShrink={0}
                color="white"
                onClick={addButton?.onClick}
              >
                <Show when={!addButton?.icon}>
                  <HiOutlinePlus size={18} strokeWidth={2.5} stroke="currentColor" />
                </Show>
                <Show when={!!addButton?.icon}>
                  {addButton?.icon}
                </Show>
              </FaceliftIconButton>
            </Tooltip>
          </Show>
        </HStack>

        <Box px="2" py="1" maxHeight={'70vh'} overflow="hidden">
          {children}
        </Box>
      </Box>
    </Box>
  </Box>
  );
}

export default Panel